import type { Column } from '@talos/kyoko';
import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import type { FundingRateMatrixExchange, FundingRateMatrixRow } from './FundingRateBlotter.types';
import { MatrixRateCell } from './fundingRateBlotterCells';

/** AG Grid header class on the exchange columns; FundingRateMatrixView centers its label to match the cells. */
export const MATRIX_RATE_HEADER_CLASS = 'funding-rate-matrix-rate-header';

/**
 * Matrix columns: a pinned Asset column (icon + symbol) plus one column per exchange in the data.
 * Exchange cells render a magnitude-scaled heat chip with a hover tooltip. Columns aren't sortable —
 * the Matrix keeps assets in their configured order.
 */
export function useFundingRateMatrixColumns(
  exchanges: FundingRateMatrixExchange[],
  maxAbsRate: number,
  periodLabel: string
): Column<unknown, FundingRateMatrixRow>[] {
  const theme = useTheme();

  return useMemo(() => {
    const positiveColor = theme.colors.green.default;
    const negativeColor = theme.colors.red.default;

    const assetColumn: Column<unknown, FundingRateMatrixRow> = {
      type: 'currency',
      field: 'symbol',
      title: 'Asset',
      pinned: 'left',
      width: 140,
      sortable: false,
    };

    const exchangeColumns = exchanges.map<Column<unknown, FundingRateMatrixRow>>(exchange => ({
      type: 'custom',
      id: `rate-${exchange.id}`,
      title: exchange.displayName,
      flex: 1,
      minWidth: 110,
      sortable: false,
      params: {
        // Center the header label to line up with the centered rate cells (see FundingRateMatrixView).
        headerClass: MATRIX_RATE_HEADER_CLASS,
        valueGetter: ({ data }: ValueGetterParams<FundingRateMatrixRow>) =>
          data?.cellsByExchange[exchange.id]?.rate ?? null,
        cellRenderer: ({ data, value }: ICellRendererParams<FundingRateMatrixRow, number | null>) => (
          <MatrixRateCell
            value={value ?? null}
            maxAbsRate={maxAbsRate}
            positiveColor={positiveColor}
            negativeColor={negativeColor}
            symbol={data?.symbol ?? ''}
            exchangeDisplayName={exchange.displayName}
            openInterestUsd={data?.cellsByExchange[exchange.id]?.openInterestUsd ?? null}
            periodLabel={periodLabel}
            tooltipPadding={theme.spacing12}
          />
        ),
      },
    }));

    return [assetColumn, ...exchangeColumns];
  }, [exchanges, maxAbsRate, periodLabel, theme.colors.green.default, theme.colors.red.default, theme.spacing12]);
}
